import React, { ChangeEvent, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, InputValue, Select } from '@xsolla/ui-core';
import { PlusCircle, Trash } from '@xsolla/ui-icons';

import { MergeTag } from '../../../../api/users/mailchimp/types';
import { FormFooter } from '../../../ui';
import {
  AddFields,
  AddFieldsContainer,
  Container,
  ErrorContainer,
  ErrorContent,
  ErrorMessage,
  InputContainer,
  MergeTagRow,
  RowContent,
  SelectContainer,
  StyledInput
} from './style';

type Props = {
  data: MergeTag[];
  handleChange: (index: number, field: keyof MergeTag, value: string) => void;
  deleteRow: (index: number) => void;
  addRow: () => void;
  onSubmit: () => void;
  onClose: () => void;
  hasData: boolean;
  fetching: boolean;
  error: boolean;
  submitText: string;
};

const FormComponent = ({
  data,
  handleChange,
  deleteRow,
  addRow,
  onSubmit,
  onClose,
  hasData,
  fetching,
  error,
  submitText
}: Props): JSX.Element => {
  const intl = useIntl();
  const [mergeTagErrors, setMergeTagErrors] = useState<string[]>(Array(data.length).fill(''));
  const [fieldLabelErrors, setFieldLabelErrors] = useState<string[]>(Array(data.length).fill(''));

  const hasEmptyValues = (): boolean => {
    return data.some(
      (item) => !item.field_to_export || !item.mailchimp_field_label || !item.mailchimp_merge_tag
    );
  };

  const allOptions = [
    {
      value: 'nickname',
      children: intl.formatMessage({
        id: 'social-connection.settings.user-data.nickname'
      })
    },
    {
      value: 'birth_date',
      children: intl.formatMessage({
        id: 'social-connection.settings.user-data.birthday'
      })
    },
    {
      value: 'first_name',
      children: intl.formatMessage({
        id: 'user-data-base.settings.supplementary-info.field.given-name'
      })
    },
    {
      value: 'last_name',
      children: intl.formatMessage({
        id: 'user-data-base.settings.supplementary-info.field.family-name'
      })
    },
    {
      value: 'gender',
      children: intl.formatMessage({
        id: 'social-connection.settings.user-data.gender'
      })
    },
    {
      value: 'server_custom_id',
      children: intl.formatMessage({
        id: 'users.settings.general-info.login-type.server_custom_id'
      })
    }
  ];

  const getFilteredOptions = (currentIndex: number) => {
    const selectedValues = data.map((item, idx) =>
      idx !== currentIndex ? item.field_to_export : null
    );
    return allOptions.filter((option) => !selectedValues.includes(option.value));
  };

  const validateMergeTag = (value: string, index: number) => {
    if (value.length < 1 || value.length > 10) {
      setMergeTagErrors((prevErrors) => {
        const newErrors = [...prevErrors];
        newErrors[index] = intl.formatMessage({
          id: 'users.mailchimp.merge-tags-form.merge-tag.error'
        });
        return newErrors;
      });
    } else {
      setMergeTagErrors((prevErrors) => {
        const newErrors = [...prevErrors];
        newErrors[index] = '';
        return newErrors;
      });
    }
  };

  const validateFieldLabel = (value: string, index: number) => {
    if (value.length < 1 || value.length > 50) {
      setFieldLabelErrors((prevErrors) => {
        const newErrors = [...prevErrors];
        newErrors[index] = intl.formatMessage({
          id: 'users.mailchimp.merge-tags-form.field-label.error'
        });
        return newErrors;
      });
    } else {
      setFieldLabelErrors((prevErrors) => {
        const newErrors = [...prevErrors];
        newErrors[index] = '';
        return newErrors;
      });
    }
  };

  const incompleteForm = useMemo(() => {
    const hasValidationErrors =
      mergeTagErrors.some((err) => err !== '') || fieldLabelErrors.some((err) => err !== '');
    const hasEmptyFields = hasData && hasEmptyValues();

    return hasValidationErrors || hasEmptyFields;
  }, [mergeTagErrors, fieldLabelErrors, hasData, data]);

  return (
    <Container id="newComp">
      {data.map((item, index) => (
        <MergeTagRow>
          <RowContent id="merge-tag-row">
            <SelectContainer>
              <Select
                value={item.field_to_export}
                onChange={(value: InputValue | null) =>
                  handleChange(index, 'field_to_export', value as string)
                }
                options={getFilteredOptions(index)}
              />
            </SelectContainer>
            <InputContainer id="merge-tag-input1">
              <StyledInput
                type="text"
                value={item.mailchimp_field_label}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const { value } = e.target;
                  handleChange(index, 'mailchimp_field_label', value);
                  validateFieldLabel(value, index);
                }}
                hasError={!!fieldLabelErrors[index]}
                placeholder={intl.formatMessage({
                  id: 'users.mailchimp.merge-tags-form.field-label.placeholder'
                })}
              />
            </InputContainer>
            <InputContainer id="merge-tag-input2">
              <StyledInput
                type="text"
                value={item.mailchimp_merge_tag}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const { value } = e.target;
                  handleChange(index, 'mailchimp_merge_tag', value);
                  validateMergeTag(value, index);
                }}
                hasError={!!mergeTagErrors[index]}
                placeholder={intl.formatMessage({
                  id: 'users.mailchimp.merge-tags-form.merge-tag.placeholder'
                })}
              />
            </InputContainer>
            <Button look="icon" palette="brand" size="md" onClick={() => deleteRow(index)}>
              <Trash />
            </Button>
          </RowContent>
          <ErrorContent id="merge-tag-row">
            <ErrorContainer />
            <ErrorContainer>
              {fieldLabelErrors[index] && (
                <ErrorMessage id="error-message-1">{fieldLabelErrors[index]}</ErrorMessage>
              )}
            </ErrorContainer>
            <ErrorContainer>
              {mergeTagErrors[index] && (
                <ErrorMessage id="error-message-2">{mergeTagErrors[index]}</ErrorMessage>
              )}
            </ErrorContainer>
          </ErrorContent>
        </MergeTagRow>
      ))}
      <AddFieldsContainer>
        <AddFields onClick={addRow} disabled={data.length >= allOptions.length}>
          <span>
            {intl.formatMessage({
              id: 'users.mailchimp.merge-tags-form.add-tag'
            })}
          </span>
          <PlusCircle size={24} />
        </AddFields>
      </AddFieldsContainer>
      <FormFooter
        isModal
        onSubmit={hasData ? onSubmit : onClose}
        onCancel={onClose}
        fetching={fetching}
        error={error}
        submitText={submitText}
        disabled={incompleteForm}
      />
    </Container>
  );
};

export default FormComponent;
